/**
 * Brands section – fully self-contained, no theme dependencies.
 */
class BrandsComponent extends HTMLElement {
  connectedCallback() {
    this.currentFilter = 'all';
    this.brandData = new Map();

    // Cache DOM references
    this.grid = this.querySelector('.brands-grid');
    this.dialog = this.querySelector('.brands-modal');
    this.modalContent = this.querySelector('.brands-modal__inner');
    this.filterButtons = [...this.querySelectorAll('.brands-filter__button')];
    this.brandCards = [...this.querySelectorAll('.brands-grid__item')];

    // Cache brand data from hidden store
    this.querySelector('.brands-data')
      ?.querySelectorAll('[data-block-id]')
      .forEach((item) => this.brandData.set(item.dataset.blockId, item.innerHTML));

    // Bind events
    this.filterButtons.forEach((btn) =>
      btn.addEventListener('click', () => this._handleFilter(btn))
    );

    this.querySelectorAll('[data-open-modal]').forEach((btn) =>
      btn.addEventListener('click', () => this._openModal(btn.dataset.openModal))
    );

    this.querySelector('[data-close-modal]')
      ?.addEventListener('click', () => this._closeModal());

    // Close on backdrop click
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this._closeModal();
    });

    // Close on Escape
    this.dialog?.addEventListener('cancel', (e) => {
      e.preventDefault();
      this._closeModal();
    });
  }

  // ── Filtering ──────────────────────────────────────────────

  async _handleFilter(button) {
    const category = button.dataset.category;
    if (category === this.currentFilter) return;

    // Update active state
    this.filterButtons.forEach((btn) =>
      btn.classList.toggle('brands-filter__button--active', btn === button)
    );
    this.currentFilter = category;
    this.classList.toggle('brands--filtered', category !== 'all');
    this.classList.toggle('brands--all', category == 'all');

    await this._animateFilter(category);
  }

  async _animateFilter(category) {
    if (this._filtering) return;
    this._filtering = true;

    const isAll = category === 'all';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const toHide = [];
    const toReveal = [];

    this.brandCards.forEach((card) => {
      const shouldShow = isAll || card.dataset.category === category;
      const isHidden = card.classList.contains('brands-grid__item--hidden');

      if (!shouldShow && !isHidden) toHide.push(card);
      if (shouldShow && isHidden) toReveal.push(card);
    });

    // Phase 1 – fade out cards that need to hide
    if (toHide.length) {
      toHide.forEach((card) => card.classList.add('brands-grid__item--filtering-out'));
      if (!reducedMotion) await this._waitForTransition(toHide[0], 300);
      toHide.forEach((card) => {
        card.classList.remove('brands-grid__item--filtering-out');
        card.classList.add('brands-grid__item--hidden');
      });
    }

    // Phase 2 – reveal new cards (start invisible, then transition in)
    if (toReveal.length) {
      // Place them in the DOM at opacity 0
      toReveal.forEach((card) => {
        card.classList.remove('brands-grid__item--hidden');
        card.classList.add('brands-grid__item--entering');
      });

      // Force layout so the browser registers the starting state
      void this.grid.offsetHeight;

      // Stagger the fade-in by setting a transition-delay, then remove --entering
      toReveal.forEach((card, i) => {
        card.style.transitionDelay = reducedMotion ? '0ms' : `${i * 30}ms`;
        card.classList.remove('brands-grid__item--entering');
      });

      if (!reducedMotion) {
        await this._waitForTransition(toReveal[toReveal.length - 1], 400 + toReveal.length * 30);
      }

      // Clean up transition-delay
      toReveal.forEach((card) => card.style.removeProperty('transition-delay'));
    }

    this._filtering = false;
  }

  _waitForTransition(el, fallbackMs) {
    return new Promise((resolve) => {
      const onEnd = () => {
        el.removeEventListener('transitionend', onEnd);
        resolve();
      };
      el.addEventListener('transitionend', onEnd);
      setTimeout(resolve, fallbackMs);
    });
  }

  // ── Modal ──────────────────────────────────────────────────

  _openModal(blockId) {
    const content = this.brandData.get(blockId);
    if (!content || !this.dialog) return;

    this.modalContent.innerHTML = content;
    this.dialog.showModal();
    document.body.style.overflow = 'hidden';
  }

  _closeModal() {
    if (!this.dialog?.open) return;

    this.dialog.classList.add('dialog-closing');

    const onEnd = () => {
      this.dialog.classList.remove('dialog-closing');
      this.dialog.close();
      document.body.style.overflow = '';
      this.dialog.removeEventListener('animationend', onEnd);
    };

    this.dialog.addEventListener('animationend', onEnd);

    // Fallback in case animationend never fires (reduced motion, etc.)
    setTimeout(onEnd, 350);
  }
}

if (!customElements.get('brands-component')) {
  customElements.define('brands-component', BrandsComponent);
}
